// src/services/homePrivate.js
//
// Browser side of the home page's private cards (My notes, My quick links;
// UI revamp). The limits and the link rules are a MIRROR of the server's
// functions/homePrivate.js, which is what actually decides (saveHomePrivate
// refuses anything it doesn't accept); homePrivate.test.js checks the two agree.
// Pure: no Firebase. I/O is homePrivateService.js.
//
// Nothing here removes data on its own: no expiry, no reset at midnight or at
// the start of a week. A goal or link goes only when its owner deletes it.

export const LIMITS = Object.freeze({
  itemText: 140,
  itemsPerList: 50,
  notes: 2000,
  links: 12,
  linkLabel: 40,
  linkUrl: 500,
  id: 40,
  pins: 12,
});

const str = (v) => (typeof v === "string" ? v : "");
const HOST_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
// Pinned RegX pages: home sidebar shortcut ids, never addresses (same rule as the server).
const PIN_RE = /^[a-z0-9-]{1,40}$/;

/** Same rule as the server: https only (added if missing), no credentials, ≤500, real host. */
export const normalizeLinkUrl = (raw) => {
  let s = str(raw).trim();
  if (!s) return { ok: false, error: "Enter an address." };
  // "user:pw@host" or "https://user@host": sign-in details before the host.
  if (/^(?:[a-z][a-z0-9+.-]*:\/\/)?[^/?#]*@/i.test(s)) {
    return { ok: false, error: "Don't put a username or password in an address." };
  }
  if (!/^[a-z][a-z0-9+.-]*:/i.test(s)) s = `https://${s}`;
  if (!/^https:\/\//i.test(s)) return { ok: false, error: "Only https:// addresses can be saved." };
  let u;
  try {
    u = new URL(s);
  } catch {
    return { ok: false, error: "That isn't a valid address." };
  }
  if (u.protocol !== "https:") return { ok: false, error: "Only https:// addresses can be saved." };
  if (u.username || u.password || /^https:\/\/[^/?#]*@/i.test(s)) {
    return { ok: false, error: "Don't put a username or password in an address." };
  }
  const hostname = u.hostname.toLowerCase();
  if (!HOST_RE.test(hostname) && !IPV4_RE.test(hostname)) {
    return { ok: false, error: "That address needs a real website name, like example.com." };
  }
  const url = u.href;
  if (url.length > LIMITS.linkUrl) return { ok: false, error: `Addresses can be at most ${LIMITS.linkUrl} characters.` };
  return { ok: true, url, hostname };
};

export const hostnameOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};

/** A short random id (letters, digits, - and _ only, as the server requires). */
export const newId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/** A clean private doc from whatever was stored (or nothing). */
export const normalizePrivate = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const items = (l) =>
    (Array.isArray(l) ? l : [])
      .filter((it) => it && typeof it === "object" && str(it.id) && str(it.text))
      .map((it) => ({ id: it.id, text: it.text, done: it.done === true, addedAt: Number(it.addedAt) || Date.now() }));
  return {
    today: items(r.today),
    week: items(r.week),
    notes: str(r.notes),
    links: (Array.isArray(r.links) ? r.links : [])
      .filter((l) => l && typeof l === "object" && str(l.id) && str(l.url))
      .map((l) => ({ id: l.id, label: str(l.label), url: l.url })),
    pins: [...new Set((Array.isArray(r.pins) ? r.pins : []).filter((p) => typeof p === "string" && PIN_RE.test(p)))],
  };
};

/** Pin a sidebar shortcut: { ok, pins } or { ok: false, error }. Already pinned = unchanged. */
export const pinPage = (pins, id) => {
  const list = pins || [];
  if (list.includes(id)) return { ok: true, pins: list };
  if (!PIN_RE.test(str(id))) return { ok: false, error: "That page can't be pinned." };
  if (list.length >= LIMITS.pins) return { ok: false, error: `You can pin ${LIMITS.pins} pages. Unpin one to add another.` };
  return { ok: true, pins: [...list, id] };
};
export const unpinPage = (pins, id) => (pins || []).filter((p) => p !== id);

/** Add a goal: { ok, list } or { ok: false, error }. */
export const addGoal = (list, text, now = Date.now()) => {
  const t = str(text).trim();
  if (!t) return { ok: false, error: "Type a goal first." };
  if (t.length > LIMITS.itemText) return { ok: false, error: `Goals can be at most ${LIMITS.itemText} characters.` };
  if ((list || []).length >= LIMITS.itemsPerList) {
    return { ok: false, error: `A list can hold ${LIMITS.itemsPerList} goals. Delete some to add more.` };
  }
  return { ok: true, list: [...(list || []), { id: newId("g"), text: t, done: false, addedAt: now }] };
};

export const toggleGoal = (list, id) => (list || []).map((it) => (it.id === id ? { ...it, done: !it.done } : it));
export const deleteGoal = (list, id) => (list || []).filter((it) => it.id !== id);

/** For display: open goals first (oldest first), then done ones (oldest first). */
export const sortGoals = (list) =>
  [...(list || [])].sort((a, b) => Number(a.done) - Number(b.done) || a.addedAt - b.addedAt);

export const openCount = (list) => (list || []).filter((it) => !it.done).length;

/** Add a quick link: { ok, links } or { ok: false, error }. Label defaults to the hostname. */
export const addLink = (links, { label, url }) => {
  if ((links || []).length >= LIMITS.links) return { ok: false, error: `You can keep ${LIMITS.links} links. Remove one to add another.` };
  const norm = normalizeLinkUrl(url);
  if (!norm.ok) return norm;
  const l = str(label).trim() || norm.hostname;
  if (l.length > LIMITS.linkLabel) return { ok: false, error: `Link names can be at most ${LIMITS.linkLabel} characters.` };
  return { ok: true, links: [...(links || []), { id: newId("l"), label: l, url: norm.url }] };
};

export const removeLink = (links, id) => (links || []).filter((l) => l.id !== id);
